

  
const content = `# SecureFile Limitations
It is intended for OnData SecureFile to provide a seamless experience so that users do not need to change what they normally would do when 
interacting with files and documents. Below are the specific items that may impact user experience when working with OnData SecureFile 
protected files (OSF files, with the \`.osf\` file extension).  
    
#### 1. Open Encrypted OSF Files from Windows Explorer
There are two approaches to open an OSF file from Windows Explorer:  
a. Double click on the OSF file and the file will be opened by the default application based on the original file extension. For example, a file 
named MyFile.docx.osf will be opened by Microsoft Word because the original file extension \`.docx\` is associated with the Microsoft Word application.  
  
b. Select an application from the \`Open with >\` submenu in the expanded context menu by right clicking on an OSF file. Please note that on 
Windows 11 the context menu is collapsed by default. Please do not select any application from the default \`Open with >\` submenu. Instead, please 
expand the context menu first by choosing \`Show more options\`, then select an application from the \`Open with >\` submenu. If you would like to 
always show the expanded context menu, you may run the following command from an administrative command prompt, then logout Windows and login again. 

\`\`\`bash
reg add HKCU\\Software\\Classes\\CLSID\\{86ca1aa0-34aa-4e8b-a509-50c905bae2a2}\\InprocServer32 /ve /d "" /f

\`\`\`

Please **note** that the OSF files cannot be properly opened by any of the following approaches:  
a. Drag and drop an OSF file into an application window, such as drag a file named MyFile.txt.osf into a Notepad application window.  
  
b. Launch an application and use the application to open a selected OSF file. For example, launch Microsoft Word and go to \`Open\` -> \`Browse\` to 
select a file named MyFile.docx.osf. 
  
#### 2. Open Encrypted OSF Files from Teams and SharePoint
If you have an OSF file stored in Teams or SharePoint, you can directly open the OSF file for viewing or editing following the steps below:  
a. Login to the OnData SecureFile App with your account credentials on your computer  
b. Ensure you have the Microsoft OneDrive application installed on your computer  
c. Ensure you have logged into the Microsoft OneDrive application. If not, you will be prompted to do so when trying to open files  
d. Go to Microsoft Teams or SharePoint, and navigate to the OSF file you would like to open  
e. Place your mouse over the OSF file and click on the ellipsis (...) to show more actions for this file  
f. From the pop-up menu, select \`Open\` -> \`Open in app\` to open the OSF file  
  
If you make any changes to the document, please make sure you save the file. Please **note** that if multiple users open the same OSF file at the same 
time for editing purposes, the last user who saves the file will overwrite the changes made by the other user(s).  
    
#### 3. Save Encrypted OSF Files
When an OSF file is properly opened by an application, you can use the \`Save\` menu item to save any updates you have made to the file. Please **do not** 
use the \`Save As\` or any other options to save the updated. Otherwise, the file would be saved in clear text without encryption.  
  
For Office documents, there are many other ways to save a file, besides \`Save\` and \`Save As\`. Only the \`Save\` operation will save the updates to the 
original OSF file and the content will continue to be encrypted. The \`Save As\` operation and any other operations below could result in a file saved 
in clear text and **should be avoided.**  
a. Turn on \`AutoSave\`, and you will be prompted to save a copy of the file in clear text to \`One Drive\`.  
b. Select \`File\` -> \`Share\`, and you will be prompted to save a copy of the file in clear text to \`One Drive\`, or email a copy of the file in clear
text in Office document format or PDF format.  
c. Select \`File\` -> \`Info\` -> \`Version History\`, and you will be prompted to save a copy of the file in clear text to \`One Drive\`.  
d. After recovering an unsaved document using \`File\` -> \`Info\` -> \`Manage Documents\` -> \`Recover Unsaved Documents\`, use the \`File\` -> \`Info\` 
-> \`Manage Documents\` -> \`Save As\` option will save a copy of the recovered file in clear text.  
e. Select \`File\` -> \`Export\` -> \`Create PDF/XPS Document\`, and you will be prompted to create a copy of the PDF/XPS file in clear text.  
f. Select \`File\` -> \`Export\` -> \`Change File Type\`, and you will be prompted to save a copy of the file in clear text.  
  
#### 4. Cannot Encrypt Read-Only Files
If a file is marked as read-only, OnData SecureFile won't encrypt that file. To allow SecureFile to encrypt that file, you can remove the read-only 
attribute of that file.  
  
#### 5. Clean Up of Temporary Files
For files opened by certain applications, such as Windows Photo Viewer, Microsoft Edge and Chrome browser applications, the temporary files won't 
be automatically cleaned up when the corresponding OSF files are closed. When you logout of the SecureFile App, it will prompt you to close all 
OSF files. Please ensure all OSF files have been closed and click on "All Files Above Are Closed" to logout.  
  
#### 6. Data Access Role Changes May Require Login Again
If a user's data access roles have been changed at the Identity Provider, or at the OnData Portal if there is no integration with an Identity 
Provider, that change may not take effect until the user logout of OnData SecureFile and login again.  
  
#### 7. Do Not Change Default Application for OSF Files
The OSF files are opened by OnData SecureFile by default. Please do not change the default application for the \`.osf\` file extension. If the default 
application for the \`.osf\` file extension is set to an application other than OnData SecureFile, please reset it back to OnData SecureFile in 
\`Settings\` -> \`Apps\` -> \`Default apps\`.  
  
#### 8. Do Not Edit OSF Files in Multiple Editors Concurrently
If you would like to edit an OSF file, please use only one editor application to edit the file. And **do not** open it in multiple editors concurrently 
to make changes. Otherwise, the changes made in one editor may be overwritten by the changes made in the other editors.  
  
#### 9. Overlay Icon May Not Display
In Windows Explorer, the encrypted OSF files will show an overlay OnData icon at the bottom left corner of the original file icon, to indicate that 
the file is protected with OnData SecureFile. However, the overlay icon may not show up when the OSF files are inside a zip package, in an FTP 
server, inside a OneDrive folder, or at other places where another overlay icon may be used for files. The file extension of \`.osf\` indicates that a 
file is protected with OnData SecureFile.  
  
#### 10. File Shortcut
If you have a shortcut pointing to a file and that file is subsequently encrypted by OnData SecureFile, the shortcut may not work anymore because 
the original file is deleted after the file is encrypted. Similarly, if you have a shortcut to an encrypted OSF file and later you decide to decrypt 
that file and choose to delete the encrypted OSF file, that shortcut may not work anymore.  
  
#### 11. Shared Files on File Servers
If you use SecureFile on your local computer to operate on a file that is shared from a remote computer or file server, please note the following 
items:  
a. The File Operation Log and the File Access Log on the OnData Portal will display your local computer name as where the file is operated from, instead 
of the remote computer or file server.  
b. The file may be opened in read-only mode if someone else has opened it for editing already.  
c. Be sure to save and close all open OSF files before logging out or restarting your computer.  
  
#### 12. Do Not Edit OSF Files in Compressed Packages
You can double-click an OSF file inside a compressed/zip package to open it, but you should not edit it because the changes cannot be saved back to the compressed 
package. If you would like to edit an OSF file inside a compressed/zip package, please extract it out of the package first, then edit and save the changes.  
  
#### 13. Recover Shared Files in Read-Only Mode
For a shared file, if a user has opened it for editing, but goes offline without closing it, another user may open the same file for editing. In that case, the 
original user who has opened the file for editing cannot save the changes back to the shared file after it is recovered. In order to preserve the changes, the 
original user can save the temporary file to another location, remove the file entry from the recovery page, open the shared file again, and manually merge the 
changes back to the shared file.  
  
`
  
export default content;
